import type { ListProps, OptionalQueryProps, PickerProps, QueryProps } from "shared/react";
import type { CreatorEntity, EarnedData, RewardData, RewardType } from "shared/schema";
import { call } from "shelving";
import { Button, Grid, closeEventDialog, useDialog } from "shared/react";
import { EARNED_MAX, REWARD_MAX } from "shared/schema";
import { RewardCreateForm } from "../create/RewardForm.js";
import { useQuery } from "../util/db.js";
import { USER } from "../util/user.js";
import { BackgroundIcon, RawBackgroundIcon } from "./BackgroundIcon.js";
import { BACKGROUND_GRID_MODS, REWARD_GRID_MODS } from "./RewardGrid.js";
import { RawRewardIcon, RewardIcon } from "./RewardIcon.js";

/** Pick a reward based on a reward query. */
export function RewardPicker({
	type, //
	query,
	onPick,
	mods = type === "background" ? BACKGROUND_GRID_MODS : REWARD_GRID_MODS,
	...props
}: { type: RewardType } & PickerProps<string> & QueryProps<RewardData>): React.ReactElement {
	return (
		<Grid
			store={useQuery("reward", query)}
			mapper={({ id, name, image, hue }) => (
				<button
					key={id}
					type="button"
					className={type === "background" ? "background" : "icon"}
					onClick={e => {
						call(onPick, id);
						closeEventDialog(e);
					}}
				>
					{type === "background" ? <RawBackgroundIcon image={image} name={name} hue={hue} /> : <RawRewardIcon type={type} image={image} name={name} />}
				</button>
			)}
			empty={`No ${type}s`}
			more={`Show more ${type}s`}
			mods={mods}
			{...props}
		/>
	);
}

/** Pick a reward based on an earned query. */
export function EarnedRewardPicker({
	type, //
	query,
	onPick,
	mods = type === "background" ? BACKGROUND_GRID_MODS : REWARD_GRID_MODS,
	...props
}: { type: RewardType } & QueryProps<EarnedData> & PickerProps<string>): React.ReactElement {
	return (
		<Grid
			store={useQuery("earned", query)}
			mapper={({ rid }) => (
				<button
					key={rid}
					type="button"
					className={type === "background" ? "background" : "icon"}
					onClick={e => {
						closeEventDialog(e);
						call(onPick, rid);
					}}
				>
					{type === "background" ? <BackgroundIcon id={rid} /> : <RewardIcon id={rid} type={type} />}
				</button>
			)}
			empty={`No earned ${type}s`}
			more={`Show more ${type}s`}
			mods={mods}
			{...props}
		/>
	);
}

export function TrajectoryEarnedRewardPicker({
	type, //
	query = {
		uid: USER.data.uid,
		type,
		$order: "!created",
		$limit: EARNED_MAX,
	},
	...props
}: { type: RewardType } & PickerProps<string> & OptionalQueryProps<EarnedData>): React.ReactElement {
	return (
		<>
			<section>
				<h2>{`Trajectory ${type}s`}</h2>
				<RewardPicker //
					type={type}
					query={{
						unlocked: true,
						$limit: REWARD_MAX,
					}}
					empty={`No Trajectory ${type}s`}
					{...props}
				/>
			</section>
			<section>
				<h2>{`Earned ${type}s`}</h2>
				<EarnedRewardPicker //
					type={type}
					query={query}
					{...props}
				/>
			</section>
		</>
	);
}

export function TrajectoryCreatedRewardPicker({
	type,
	creator,
	query = {
		creator,
		type,
		$order: "!created",
		$limit: REWARD_MAX,
	},
	onPick,
	...props
}: {
	type: RewardType;
	creator: CreatorEntity;
} & PickerProps<string> &
	OptionalQueryProps<RewardData> &
	ListProps): React.ReactElement {
	const showDialog = useDialog();
	return (
		<>
			<section>
				<h2>{`Trajectory ${type}s`}</h2>
				<RewardPicker //
					type={type}
					query={{
						creatable: true,
						$limit: REWARD_MAX,
					}}
					empty={`No Trajectory ${type}s`}
					onPick={onPick}
					{...props}
				/>
			</section>
			<section>
				<h2>{`Created ${type}s`}</h2>
				<Button
					onClick={e => {
						closeEventDialog(e);
						showDialog(
							<article className="modal">
								<h2>{`Create ${type}`}</h2>
								<RewardCreateForm type={type} onReward={onPick} data={{ creator }} />
							</article>,
						);
					}}
				>
					{`Create ${type}`}
				</Button>
				<RewardPicker //
					type={type}
					query={query}
					empty={`No created ${type}s`}
					onPick={onPick}
					{...props}
				/>
			</section>
		</>
	);
}
